'use client';

import { Quote } from '../data/quotesInterface';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { getFilteredQuotes } from '@/lib/quoteAPIHelper';
import { useToast } from '@/hooks/use-toast';

import {
    Pagination,
    PaginationContent,
    PaginationItem,
    PaginationLink,
    PaginationNext,
    PaginationPrevious,
} from "@/components/ui/pagination";
import { logAnalyticsEvent } from '@/lib/analytics';
import { useState, useEffect, useCallback } from 'react';

interface QuotesListProps {
    quotes: Quote[];
    quotesnum?: number;
    categoryFilter: string[];
    authorFilter: string[];
    filter: string[];
}
export default function QuotesList({ quotes, quotesnum, categoryFilter, authorFilter, filter }: QuotesListProps) {

    const { toast } = useToast();

    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [searchResults, setSearchResults] = useState<Quote[]>(quotes || []);
    const [isFirstLoad, setIsFirstLoad] = useState(true);

    const resultsPerPage = 9;
    // Calculate pagination
    const totalPages = Math.ceil(quotesnum / resultsPerPage);
    const indexOfLastResult = currentPage * resultsPerPage;
    const indexOfFirstResult = indexOfLastResult - resultsPerPage;

    // Generate page numbers
    const getPageNumbers = () => {
        const pageNumbers = [];

        if (totalPages <= 5) {
            // Show all pages if 5 or fewer
            for (let i = 1; i <= totalPages; i++) {
                pageNumbers.push(i);
            }
        } else {
            // Always show first page
            pageNumbers.push(1);

            // Show ellipsis or pages around current
            if (currentPage <= 3) {
                pageNumbers.push(2, 3, 4, '...');
            } else if (currentPage >= totalPages - 2) {
                pageNumbers.push('...', totalPages - 3, totalPages - 2, totalPages - 1);
            } else {
                pageNumbers.push('...', currentPage - 1, currentPage, currentPage + 1, '...');
            }

            // Always show last page
            pageNumbers.push(totalPages);
        }

        return pageNumbers;
    };
    const handleQuoteSelect = (quote: Quote) => {
        // setSelectedQuote(quote);
        // // Select a random image for the quote
        // setSelectedImage(quote.image);
        const siteUrl = process.env.NEXT_PUBLIC_SITE_URL;
        const quoteUrl = `${siteUrl}/quote/${quote._id}`;
        window.open(quoteUrl, '_blank');
    };
    const handleSearch = useCallback(async () => {
        logAnalyticsEvent('list_page_click');
        let toastDismiss: (() => void) | null = null;
        const longRequestTimeOutId = setTimeout(() => {
            toastDismiss = toast({
                title: "Loading...",
                description: "This is taking longer than usual. Please wait.",
            }).dismiss;
        }, 1000);

        const results: { quotes: Quote[], totalQuotes: number } = await getFilteredQuotes(categoryFilter, authorFilter, '', currentPage, false);
        clearTimeout(longRequestTimeOutId);
        if (toastDismiss) {
            toastDismiss();
        }
        setSearchResults(results.quotes);
    }, [currentPage]);

    // Update search when filters change
    useEffect(() => {
        if (isFirstLoad) {
            return;
        }
        handleSearch();
    }, [currentPage]);

    useEffect(() => {
        setIsFirstLoad(false);
    }, []);

    return (
        <div className="min-h-screen bg-gradient-to-br from-green-50 via-blue-50 to-purple-50 py-8 px-4">
            <div className="max-w-6xl mx-auto">
                <div className="flex items-center mb-8">
                    <h1 className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-green-600 to-blue-600 bg-clip-text text-transparent">
                        {filter[0]} Quotes
                    </h1>
                </div>


                <>
                    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 auto-rows-max">
                        {searchResults.map((quote) => (
                            <Card
                                key={quote._id}
                                className="p-4 cursor-pointer hover:shadow-md transition-all hover:-translate-y-1 hover:bg-white/80 relative overflow-hidden h-fit"
                                onClick={() => handleQuoteSelect(quote)}
                            >
                                <div className="quote-text text-lg mb-3 line-clamp-3 min-h-[72px]">{quote.content}</div>
                                <div className="flex justify-between items-center">
                                    <div className="quote-author text-sm text-green-700">— {quote.author}</div>
                                    {quote.tags.length > 0 && (
                                        <Badge variant="outline" className="text-xs border-green-200">
                                            {quote.tags[0]}
                                        </Badge>
                                    )}
                                </div>
                            </Card>
                        ))}
                    </div>

                </>

            </div>

            {/* Pagination */}
            {totalPages > 1 && (
                <div className="mt-8">
                    <Pagination>
                        <PaginationContent>
                            <PaginationItem>
                                <PaginationPrevious
                                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                    className={currentPage === 1 ? "pointer-events-none opacity-50" : "cursor-pointer"}
                                />
                            </PaginationItem>

                            {getPageNumbers().map((page, index) => (
                                <PaginationItem key={index} className="hidden sm:inline-block">
                                    {page === '...' ? (
                                        <span className="px-4 py-2">&hellip;</span>
                                    ) : (
                                        <PaginationLink
                                            isActive={currentPage === page}
                                            onClick={() => typeof page === 'number' && setCurrentPage(page)}
                                            className="cursor-pointer"
                                        >
                                            {page}
                                        </PaginationLink>
                                    )}
                                </PaginationItem>
                            ))}

                            <PaginationItem>
                                <PaginationNext
                                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                                    className={currentPage === totalPages ? "pointer-events-none opacity-50" : "cursor-pointer"}
                                />
                            </PaginationItem>
                        </PaginationContent>
                    </Pagination>
                    <p className="text-center text-sm text-muted-foreground mt-2">
                        Page {currentPage} of {totalPages}
                    </p>
                </div>
            )}
        </div>

    );
}